/* eslint-disable @typescript-eslint/no-unused-vars */
import { FC } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { useDrawer } from '@/_shared/components';
import { Charts } from '@/_shared/components/Charts';
import { Column, usePaginatedTable } from '@/_shared/components/Table';
import { PaginatedTable } from '@/_shared/components/Table/PaginatedTable';
import { StyledTable, StyledTableDrawer } from '@/_shared/components/Table/StyledTable';
import { ValueInTable } from '@/_shared/components/ValueInTable';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { postEmployerCreditsCall } from '@/credits/state/api';
import { ClusterType } from '@/swagger/constants/credits-enums';
import { getDepartmentTeamCreditsCall } from '../../../state';

export const ClusterDataTeam: FC = () => {
  const t = useTranslations('departments.tabs.credits.cluster');

  const { genericResponsiveValue } = useResponsiveStyle();

  const { clusterUsers } = getDepartmentTeamCreditsCall.hook();

  const { isOpen, close, ...drawerProps } = useDrawer();

  const paginedProps = usePaginatedTable({
    rowData: clusterUsers,
    pageSize: 30,
    totalItems: clusterUsers.length,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  const columns: Column[] = [
    {
      key: 'cluster',
      titleTooltip: t('table.clusterTooltip'),
      title: t('table.cluster'),
      renderValue: (item: { clusterName: ClusterType }) => (
        <ValueInTable>
          <Box width='100%'>
            <Box fontWeight='500'>{t(`${item.clusterName}.label`)}</Box>
            <Box>
              <Typography variant='p3/light'>{t(`${item.clusterName}.subLabel`)}</Typography>
            </Box>
          </Box>
        </ValueInTable>
      ),
    },
    {
      key: 'media',
      title: t('table.media'),
      renderValue: (item: { avgUsedCredits: number }) => (
        <ValueInTable>
          <Box width='100%'>
            <Box textAlign='center' fontWeight='500'>
              {item.avgUsedCredits}
            </Box>
            <Box textAlign='center'>
              <Typography variant='p3/light'>{t('table.mediaLabel')}</Typography>
            </Box>
          </Box>
        </ValueInTable>
      ),
    },
    {
      key: 'person',
      title: t('table.person'),
      renderValue: (item: { usersNumber: number }) => (
        <ValueInTable>
          <Box width='100%'>
            <Box textAlign='center' fontWeight='500'>
              {item.usersNumber}
            </Box>
            <Box textAlign='center'>
              <Typography variant='p3/light'>{t('table.personLabel')}</Typography>
            </Box>
          </Box>
        </ValueInTable>
      ),
    },
  ];

  return (
    <Box mt='m'>
      <Typography mb='xs' variant='h2'>
        {t('title')}
      </Typography>
      <Typography variant='p3/light'>{t('descriptionTeam')}</Typography>
      <Box
        mt='xxl'
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        alignItems='stretch'
      >
        <Box
          flex='1'
          mr={genericResponsiveValue({
            breakPoints: { desktop: 's' },
            defaultValue: 'none',
          })}
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'm',
          })}
          display='flex'
          alignItems='center'
        >
          <Charts.BarChartComponent
            hasNoXAxis
            domainHasInPercentage={false}
            data={clusterUsers.map(cluster => {
              return {
                name: '',
                value: cluster.usersNumber,
                label: cluster.usersNumber
                  ? `${cluster.usersNumber} ${t('labelPerson', { clusterName: cluster.clusterName })}`
                  : `0 ${t('labelPerson', { clusterName: cluster.clusterName })}`,
              };
            })}
          />
        </Box>
        <Box flex='1'>
          <Card>
            <StyledTable hasElement drawer={{ ...drawerProps, isOpen, close, instances: drawers }}>
              <PaginatedTable
                header-data-testid='table-team-header'
                row-data-testid='table-employer-row'
                columns={columns}
                pageSize={3}
                call={{ ...postEmployerCreditsCall }}
                onRowClick={() => {
                  /* empty */
                }}
                rowWrapperProps={(employee: string) => {
                  return employee === ''
                    ? {
                        backgroundColor: 'grey/dark/background',
                        borderRadius: '10px',
                        position: 'relative',
                      }
                    : {};
                }}
                paginedProps={paginedProps}
                skeletonProps={{
                  hasHeaderRow1: false,
                  hasHeaderRow2: true,
                  rows: 1,
                }}
                hasHover
                hidePagination
              />
            </StyledTable>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
